import React, { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { NavLink, Link } from "react-router-dom";
import { QButton } from "./QButton";
import { Container } from "./Container";
import { NAV_LINKS, CONTACT, QVOLT_LOGO } from "../../lib/content";

const Logo = () => (
  <Link
    to="/"
    className="inline-flex items-center"
    data-testid="header-logo-link"
  >
    <img
      src={QVOLT_LOGO}
      alt="QVolt Solar"
      width={184}
      height={42}
      fetchPriority="high"
      data-testid="brand-logo"
      className="h-9 md:h-10 w-auto block select-none"
      draggable={false}
    />
  </Link>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 bg-white border-b border-border transition-shadow ${
        scrolled ? "shadow-sm" : ""
      }`}
    >
      <Container className="flex items-center justify-between h-16 md:h-[72px] gap-6">
        <Logo />

        <nav
          aria-label="Primary"
          className="hidden md:flex items-center gap-8"
          data-testid="header-nav"
        >
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.label}
              to={l.href}
              end
              data-testid={`header-nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative text-[14px] transition-colors qv-focus rounded-sm ${
                  isActive
                    ? "text-ink nav-link-active"
                    : "text-ink/80 hover:text-brand-blue"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3 lg:gap-4">
          <a
            href={`tel:${(CONTACT.phone || "").replace(/\s/g, "")}`}
            data-testid="header-phone"
            className="text-[13px] text-subtle hover:text-ink transition-colors hidden xl:inline-flex items-center gap-1.5 qv-focus rounded-sm"
          >
            <Phone className="h-3.5 w-3.5" strokeWidth={1.75} />
            {CONTACT.phone}
          </a>
          <QButton
            variant="primary"
            size="sm"
            as={Link}
            to="/contact"
            data-testid="header-cta-quote"
          >
            Get a free quote
          </QButton>
        </div>

        <button
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          data-testid="header-mobile-toggle"
          className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full text-ink hover:bg-muted transition-colors qv-focus"
        >
          {open ? (
            <X className="h-5 w-5" strokeWidth={1.75} />
          ) : (
            <Menu className="h-5 w-5" strokeWidth={1.75} />
          )}
        </button>
      </Container>

      {open && (
        <div
          className="md:hidden border-t border-border bg-white"
          data-testid="header-mobile-drawer"
        >
          <Container className="py-6 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.label}
                to={l.href}
                end
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `py-3 text-[15px] border-b border-border last:border-0 qv-focus rounded-sm ${
                    isActive
                      ? "text-brand-blue font-medium"
                      : "text-ink hover:text-brand-blue"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="pt-4 flex flex-col gap-3">
              <QButton
                variant="primary"
                size="md"
                as={Link}
                to="/contact"
                onClick={() => setOpen(false)}
                data-testid="mobile-cta-quote"
              >
                Get a free quote
              </QButton>
              <a
                href={`tel:${(CONTACT.phone || "").replace(/\s/g, "")}`}
                className="text-center text-[14px] text-subtle"
                data-testid="mobile-phone"
              >
                Call {CONTACT.phone}
              </a>
            </div>
          </Container>
        </div>
      )}
    </header>
  );
};

export default Header;
